<script>
import { createEventDispatcher, tick } from 'svelte';
import { format, startOfDay, addHours, addMinutes, setHours, setMinutes, startOfHour, endOfHour } from 'date-fns';
import { tooltipTop } from '../tooltip';
import { today, minute } from '../data/datetime';


export let selected = $minute;
export let interval = 5;
export let min = null;
export let max = null;
const dispatch = createEventDispatcher();

const types = {
  hour: {
    isSelected: (selected, unit) => selected && format(selected, 'h') == unit,
    list: () => [ 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12 ],
    display: unit => unit,
    getter(unit) {
      const time = selected || $minute;
      const current = time.getHours();
      if (current < 12 && unit === 12) unit = 0;
      if (current >= 12 && unit !== 12) unit += 12;
      return current === unit ? time : setHours(time, unit);
    },
    setter(unit) {
      let time = getter(unit);
      if (!selected || time.getTime() !== selected.getTime()) {
        if (min && time < min) {
          if (endOfHour(time) < min) time = addHours(time, 12);
          else time = min;
        }
        if (max && time > max) {
          if (startOfHour(time) > max) time = addHours(time, -12);
          else time = max;
        }
        if (time.getTime() !== selected.getTime()) {
          selected = time;
          dispatch('select', { selected });
        }
      }
      editing = null;
    }
  },
  minute: {
    isSelected: (selected, unit) => selected && format(selected, 'm') == unit,
    list: () => {
      let minute = 0;
      const periods = [];
      while (minute < 60) {
        periods.push(minute);
        minute += interval;
      }
      return periods;
    },
    display: unit => (unit < 10 ? '0' : '') + unit,
    getter(unit) {
      const time = selected || $minute;
      const current = time.getMinutes();
      return current === unit ? time : setMinutes(time, unit);
    },
    setter(unit) {
      let time = getter(unit);
      if (!selected || time.getTime() !== selected.getTime()) {
        if (min && time < min) time = addHours(time, 1);
        if (max && time > max) time = addHours(time, -1);
        if (time.getTime() !== selected.getTime()) {
          selected = time;
          dispatch('select', { selected });
        }
      }
      editing = null;
    }
  },
  ampm: {
    isSelected: (selected, unit) => selected && (unit === 'AM' ? selected.getHours() < 12 : selected.getHours() >= 12),
    list: () => [ 'AM', 'PM' ],
    display: unit => unit,
    getter(unit) {
      const time = selected || $minute;
      const current = time.getHours() < 12 ? 'AM' : 'PM';
      return current === unit ? time : addHours(time, unit === 'AM' ? -12 : 12);
    },
    setter(unit) {
      let time = getter(unit);
      if (!selected || time.getTime() !== selected.getTime()) {
        if (min && time < min) time = addHours(time, 12);
        if (max && time > max) time = addHours(time, -12);
        if (time.getTime() !== selected.getTime()) {
          selected = time;
          dispatch('select', { selected });
        }
      }
      editing = null;
    }
  }
};

let editing;
let column;
let currentDay = startOfDay(selected || $today);

$: min = fixTime(min, interval);
$: max = fixTime(max, interval, 0);
$: selected = fixTime(maxDate(min, minDate(max, selected)), interval);

$: units = editing && types[editing].list();
$: isSelected = editing && types[editing].isSelected;
$: getter = editing && types[editing].getter;
$: setter = editing && types[editing].setter;
$: display = editing && types[editing].display;


function fixTime(time, interval, forward = 1) {
  if (!time) return time;
  if (min && time < min) time = min;
  if (max && time > max) time = max;
  const skew = time.getMinutes() % interval;
  if (skew) {
    time = addMinutes(time, interval * forward - skew)
  }
  return time;
}

async function edit(part) {
  await new Promise(res => setTimeout(res, 1));
  editing = part;
  await tick();
  const cell = column.querySelector('.column .selected');
  if (cell) cell.parentNode.scrollTop = cell.offsetTop - 3;
}

function available(unit) {
  const time = getter(unit);
  const start = editing === 'hour' ? startOfHour(time) : time;
  const end = editing === 'hour' ? endOfHour(time) : time;
  return (!min || end >= min) && (!max || start <= max);
}

function minDate(a, b) {
  return !a || b < a ? b : a;
}

function maxDate(a, b) {
  return !a || b > a ? b : a;
}
</script>

{#if currentDay}
<div class="timepicker">
  <div class="display">
    <div class="time" on:click={() => edit('hour')}>{format(selected, 'h')}</div>
    <div class="colon">:</div>
    <div class="time" on:click={() => edit('minute')}>{format(selected, 'mm')}</div>
    <div class="time" on:click={() => edit('ampm')}>{format(selected, 'a')}</div>
    {#if editing}
    <div bind:this={column} class="{editing} column">
      <div class="scroll">
        {#each units as unit}
        <button
          class="time"
          class:selected={selected && isSelected(selected, unit)}
          on:click={() => setter(unit)}>{display(unit)}</button>
        {/each}
      </div>
    </div>
    {/if}
  </div>
</div>
{/if}

<svelte:window on:click={() => editing && (editing = null)}/>

<style>
.timepicker {
  position: relative;
}
:global(.size-mobile) .timepicker {
  width: 100%;
}
.display {
  padding: 2px 1px;
  display: flex;
  align-items: center;
  background: var(--warm-grey-050);
  border: 1px solid var(--warm-grey-100);
}
.colon {
  height: 32px;
  text-align: center;
  padding: 0 2px;
  font-size: 16px;
  line-height: 32px;
}
.time {
  display: block;
  margin: 1px 1px 2px;
  min-width: 38px;
  height: 32px;
  padding: 0 8px;
  font-size: 16px;
  line-height: 32px;
  text-align: center;
  color: var(--warm-grey-600);
  background: #fff;
  box-shadow: 0 1px var(--warm-grey-200);
  border: none;
  border-radius: 0;
  cursor: pointer;
  user-select: none;
  transition: box-shadow .1s linear, color .1s linear;
}
.time:not([disabled]):hover {
  position: relative;
  color: var(--warm-grey-700);
  box-shadow: 0 1px 3px var(--warm-grey-300);
}
/* .time[disabled], .time[disabled]:hover {
  cursor: default;
  color: var(--warm-grey-100);
  background: none;
  box-shadow: none;
} */
.time.selected, .time.selected:hover {
  position: relative;
  color: var(--cyan-700);
  outline: 3px double var(--cyan-400);
  outline-offset: -2px;
}
.time:not([disabled]):active {
  outline: none;
  box-shadow: inset 0 1px 3px var(--warm-grey-300);
}
.column {
  position: absolute;
  z-index: 1;
  top: 1px;
  left: 0;
  background: var(--warm-grey-050);
  padding: 1px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, .2), 0 2px 6px rgba(0, 0, 0, .2);
}
.column .scroll {
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  max-height: 200px;
  padding: 1px;
  scrollbar-width: none;  /* Firefox */
}
.column::after {
  content: '';
  position: absolute;
  left: 0;
  right: 0;
  top: 0;
  bottom: 0;
  box-shadow: inset 0 0 1px rgba(0, 0, 0, .2);
  pointer-events: none;
}
.column .scroll::-webkit-scrollbar,
.column .scroll::-webkit-scrollbar-track,
.column .scroll::-webkit-scrollbar-thumb {
  background: transparent;
  width: 0;
  height: 0;
}
.column.minute {
  left: 48px;
}
.column.ampm {
  left: 88px;
}
</style>
